import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useDateRange } from "@/lib/dateRange";
import { useChannel, channelParam } from "@/lib/channel";
import PageHeader from "@/components/PageHeader";
import Card from "@/components/Card";
import { Button } from "@/components/ui/button";
import OrderFormDialog from "@/components/OrderFormDialog";
import { fmtDateTime } from "@/lib/format";
import { useCurrency } from "@/lib/currency";
import { Input } from "@/components/ui/input";
import { Search, Plus, Trash2, RotateCcw } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const STATUS_COLORS = {
  completed: "bg-[#34C759]/10 text-[#1B7B36]",
  pending: "bg-[#FF9500]/10 text-[#B15C00]",
  refunded: "bg-[#8E8E93]/10 text-[#3C3C43]",
  cancelled: "bg-[#FF3B30]/10 text-[#9F1F17]",
};

const CHANNEL_STYLES = {
   external_api: { label: "External API", cls: "bg-[#0A84FF]/10 text-[#0055CC]" },
   manual:      { label: "In studio",       cls: "bg-[#8E8E93]/10 text-[#3C3C43]" },
};

function ChannelBadge({ channel }) {
  const s = CHANNEL_STYLES[channel] || { label: channel || "—", cls: "bg-black/5 text-[#3C3C43]" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${s.cls}`}>
      {s.label}
    </span>
  );
}

export default function Orders() {
  const { range } = useDateRange();
  const { channel } = useChannel();
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [savingRefund, setSavingRefund] = useState(false);
  const openRefund = (o) => {
    setRefundAmount(o.refund_total ? String(o.refund_total) : "");
    setRefundTarget(o);
  };

  const saveRefund = async () => {
    const amt = refundAmount === "" ? 0 : +refundAmount;
    if (Number.isNaN(amt) || amt < 0) return toast.error("Refund must be a positive amount");
    if (amt > refundTarget.total) return toast.error(`Refund cannot exceed the order total (${fmtMoney(refundTarget.total)})`);
    setSavingRefund(true);
    try {
      await api.put(`/orders/${refundTarget.id}`, { ...refundTarget, refund_total: +amt.toFixed(2) });
      toast.success(amt > 0 ? `Refund of ${fmtMoney(amt)} recorded` : "Refund cleared");
      setRefundTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save refund");
    } finally {
      setSavingRefund(false);
    }
  };

  const load = () => {
    setLoading(true);
    const params = { start: range.start, end: range.end };
    if (status !== "all") params.status = status;
    if (q) params.search = q;
    const ch = channelParam(channel);
    if (ch) params.channel = ch;
    return api.get("/orders", { params }).then((r) => setOrders(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [range.start, range.end, status, q, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (o) => {
    await api.delete(`/orders/${o.id}`);
    toast.success("Order deleted");
    load();
  };

  const { fmtMoney } = useCurrency();
  const totals = useMemo(() => {
    const rev = orders
      .filter((o) => o.status !== "refunded" && o.status !== "cancelled")
      .reduce((s, o) => s + Math.max(0, o.total - o.tax - o.shipping - (o.refund_total || 0)), 0);
    return { count: orders.length, rev };
  }, [orders]);

  return (
    <div data-testid="orders-page" className="max-w-[1400px] mx-auto">
      <PageHeader
         title="Sales"
         subtitle={`${totals.count} transactions · ${fmtMoney(totals.rev)} net revenue`}
        testid="orders-header"
        action={
          <Button data-testid="new-invoice-btn" onClick={() => setDialogOpen(true)} className="rounded-full bg-black text-white hover:bg-black/85">
             <Plus className="w-4 h-4 mr-1.5" /> Record sale
          </Button>
        }
      />

      <Card>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" />
            <Input
              data-testid="orders-search"
              placeholder="Search orders or customer..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 rounded-xl border-black/10 bg-white"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="orders-status-filter" className="w-full sm:w-40 rounded-xl bg-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="data-table-scroll overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6">
          <table className="w-full text-[13px] min-w-[900px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#8E8E93]">
                <th className="py-3 px-3 font-medium">Order</th>
                <th className="py-3 px-3 font-medium">Customer</th>
                <th className="py-3 px-3 font-medium">Date</th>
                <th className="py-3 px-3 font-medium">Channel</th>
                <th className="py-3 px-3 font-medium">Status</th>
                <th className="py-3 px-3 font-medium text-right">Subtotal</th>
                <th className="py-3 px-3 font-medium text-right">Tax</th>
                <th className="py-3 px-3 font-medium text-right">Shipping</th>
                <th className="py-3 px-3 font-medium text-right">Discount</th>
                <th className="py-3 px-3 font-medium text-right">Total</th>
                <th className="py-3 px-3 font-medium text-right">Refunded</th>
                <th className="py-3 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} className="py-8 text-center text-[#8E8E93]">Loading orders…</td></tr>
              )}
              {!loading && orders.length === 0 && (
                <tr><td colSpan={12} className="py-8 text-center text-[#8E8E93]">No orders in this range.</td></tr>
              )}
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.order_number}`} className="border-t border-black/[0.04] hover:bg-black/[0.015]">
                  <td className="py-3 px-3 font-medium">{o.order_number}</td>
                  <td className="py-3 px-3">{o.customer_name}</td>
                  <td className="py-3 px-3 text-[#8E8E93]">{fmtDateTime(o.created_at)}</td>
                  <td className="py-3 px-3"><ChannelBadge channel={o.channel} /></td>
                  <td className="py-3 px-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${STATUS_COLORS[o.status] || "bg-black/5 text-[#3C3C43]"}`}>
                      {o.status}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right font-mono">{fmtMoney(o.subtotal)}</td>
                  <td className="py-3 px-3 text-right font-mono text-[#8E8E93]">{fmtMoney(o.tax)}</td>
                  <td className="py-3 px-3 text-right font-mono text-[#8E8E93]">{fmtMoney(o.shipping)}</td>
                  <td className="py-3 px-3 text-right font-mono text-[#FF3B30]">{o.discount ? `-${fmtMoney(o.discount)}` : "—"}</td>
                  <td className="py-3 px-3 text-right font-mono font-semibold">{fmtMoney(o.total)}</td>
                  <td className="py-3 px-3 text-right font-mono text-[#FF3B30]">{o.refund_total ? `-${fmtMoney(o.refund_total)}` : "—"}</td>
                  <td className="py-3 px-3 text-right whitespace-nowrap">
                    {o.channel === "manual" && (
                      <button data-testid={`order-refund-${o.order_number}`} title="Record refund" onClick={() => openRefund(o)} className="p-1.5 text-[#8E8E93] hover:text-[#0A84FF] transition-colors">
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}
                    <button data-testid={`order-delete-${o.order_number}`} onClick={() => setConfirmTarget(o)} className="p-1.5 text-[#8E8E93] hover:text-[#FF3B30] transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <OrderFormDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />

      <Dialog open={!!refundTarget} onOpenChange={(o) => !o && setRefundTarget(null)}>
        <DialogContent className="mobile-dialog rounded-3xl sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="font-display">Record refund</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <p className="text-[13px] text-[#3C3C43]/80">
              Order {refundTarget?.order_number} · total {refundTarget ? fmtMoney(refundTarget.total) : ""}.
              Enter the total amount refunded on this order. It will be netted out of revenue and profit.
            </p>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Refund amount</Label>
              <Input
                data-testid="refund-amount-input"
                type="number"
                step="0.01"
                min="0"
                max={refundTarget?.total}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                className="rounded-xl font-mono"
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setRefundTarget(null)}>Cancel</Button>
            <Button data-testid="refund-save-btn" disabled={savingRefund} onClick={saveRefund} className="rounded-full bg-black text-white hover:bg-black/85">
              {savingRefund ? "Saving…" : "Save refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete order?</AlertDialogTitle>
            <AlertDialogDescription>
              Order {confirmTarget?.order_number} will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              onClick={() => { remove(confirmTarget); setConfirmTarget(null); }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
